import React, { useState, useRef, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Box,
  InputBase,
  IconButton,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import {
  FolderRounded,
  KeyboardVoice,
  PsychologyRounded,
  SendRounded,
  LightbulbRounded,
  ArrowDropDownRounded,
  BoltRounded,
} from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { Audio } from "./Audio";
import { getFileIconImage } from "../../../../../utils/fileUtils";
import { chatAgents } from "../../../../../utils/constants";
import FilesFilterDialog from "./TagFileDialog";
import { useDispatch, useSelector } from "react-redux";
import {
  CLICK_ON_RECORD_AUDIO_BUTTON,
  SUBMIT_SEARCH_QUERY,
  TOGGLE_THINKING_MODE,
  REMOVE_TAGGED_CATEGORY,
  REMOVE_TAGGED_FILE,
  SHOW_ALL_FILES_QUERY_BOX,
} from "../../../../../utils/posthogEvents";
import { usePostHog } from "posthog-js/react";
import ThinkingModeChip from "./ThinkingModeChip";

const useStyles = makeStyles()((theme, params) => ({
  wrapperContainer: {
    borderRadius: 28,
    padding: theme.palette.mode === "dark" ? 4 : 8,
    border: "1px solid #F3E5F5",
    background:
      "linear-gradient(280deg, #E3DFFC 0.82%, #FEFEFF 50.21%, #FCF2FD 99.6%)",
    boxShadow:
      "0px 2px 4px -2px rgba(0, 0, 0, 0.08), 0px 2px 12px 1px rgba(0, 0, 0, 0.08)",
    "&:focus-within": {
      borderColor: "#9C27B0",
    },
  },
  container: {
    width: "100%",
    borderRadius: 28,
    padding: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
    boxShadow: "0px 2px 12px 1px rgba(0, 0, 0, 0.08)",
    "&:focus-within": {
      borderColor:
        theme.palette.mode === "dark" ? theme.palette.primary.main : "#9C27B0",
    },
  },
  activeSendBtn: {
    borderRadius: 8,
    cursor: "pointer",
    background: theme.palette.light,
  },
  disabledSendBtn: {
    borderRadius: 8,
    marginLeft: 12,
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  activeThinkingChip: {
    backgroundColor: "#E1BEE7",
    "& .MuiChip-icon": {
      color: "#9C27B0",
    },
    "&:hover": {
      backgroundColor: "#D1A9DF", // slightly darker shade for hover
    },
  },
}));

function QueryBox({
  isStreaming = false,
  handleQuery,
  isNewSession = false,
  queryBoxContainerRef,
}) {
  const theme = useTheme();
  const posthog = usePostHog();
  const dispatch = useDispatch();
  const queryRef = useRef(null);
  const [audioMode, setAudioMode] = useState(false);
  const [query, setQuery] = useState("");
  const chatAgent = chatAgents[0];
  const { classes } = useStyles({ isNewSession });
  const { thinkingMode } = useSelector((state) => state.account);
  // const { filters } = useSelector((state) => state.query);
  // const taggedFiles = filters.taggedFiles;
  const [taggedFiles, setTaggedFiles] = useState([]);
  const [taggedCategories, setTaggedCategories] = useState([]);
  const [showAllFiles, setShowAllFiles] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(true);
  const containerRef = useRef(null);
  const chipRefs = useRef([]);
  const taggedItems = [...taggedFiles, ...taggedCategories];

  useEffect(() => {
    focusQueryBox();
  }, [taggedFiles, queryRef]);

  useEffect(() => {
    if (containerRef.current && taggedItems.length > 0) {
      const containerWidth = containerRef.current.offsetWidth;
      const gap = 8; // Gap between chips
      const moreChipWidth = 80; // Approximate width of "+X more" chip

      let totalWidth = 0;
      let visibleCount = 0;

      // Calculate how many chips can fit based on their actual widths
      for (let i = 0; i < taggedItems.length; i++) {
        const chipElement = chipRefs.current[i];
        if (chipElement) {
          const chipWidth = chipElement.offsetWidth;
          // Add width of chip and gap
          totalWidth += chipWidth + gap;

          // If adding this chip would exceed available width (accounting for more chip)
          if (totalWidth + moreChipWidth > containerWidth) {
            break;
          }
          visibleCount++;
        }
      }

      if (!showAllFiles) {
        setVisibleCount(visibleCount);
      } else {
        setVisibleCount(taggedItems.length);
      }
    }
  }, [taggedItems, showAllFiles]);

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
  };

  const handleQuerySubmit = () => {
    const queryValue = query.trim();
    if (!queryValue) return;
    setQuery("");
    handleQuery(queryValue, taggedFiles, taggedCategories);
    posthog.capture(SUBMIT_SEARCH_QUERY);
  };

  const handleQueryKeyDown = (e) => {
    const { key, shiftKey } = e;
    const queryValue = query.trim();
    if (key === "Enter" && !shiftKey && queryValue && !isStreaming) {
      e.preventDefault();
      handleQuerySubmit();
    }
  };

  const removeTaggedFile = (fileId) => {
    // dispatch(
    //   setQueryFiltersSuccess({
    //     taggedFiles: taggedFiles.filter((f) => f.id !== fileId),
    //   })
    // );
    posthog.capture(REMOVE_TAGGED_FILE, {
      fileId: fileId,
    });
    setTaggedFiles(taggedFiles.filter((f) => f.id !== fileId));
  };

  const addTaggedFiles = (files) => {
    // dispatch(setQueryFiltersSuccess({ taggedFiles: files }));
    setTaggedFiles(files);
  };

  const addTaggedCategories = (categories) => {
    setTaggedCategories(categories);
  };

  const removeTaggedCategory = (category) => {
    posthog.capture(REMOVE_TAGGED_CATEGORY, {
      category: category,
    });
    setTaggedCategories(taggedCategories.filter((c) => c !== category));
  };

  const focusQueryBox = () => {
    if (queryRef.current) {
      queryRef.current.focus();
    }
  };

  const handleClickRecordAudio = () => {
    posthog.capture(CLICK_ON_RECORD_AUDIO_BUTTON);
    setAudioMode(true);
  };

  const handleShowAllFiles = () => {
    posthog.capture(SHOW_ALL_FILES_QUERY_BOX);
    setShowAllFiles(true);
  };

  return (
    <Box
      width={"100%"}
      ref={queryBoxContainerRef}
      className={classes.container}>
      {taggedItems.length > 0 && (
        <Box
          width={"100%"}
          ref={containerRef}
          display={"flex"}
          alignItems={"center"}
          flexWrap={"wrap"}
          gap={1}
          mb={1}
          sx={showAllFiles ? { maxHeight: 80, overflow: "scroll" } : {}}>
          {taggedItems
            .slice(0, visibleCount >= 1 ? visibleCount : taggedItems.length)
            .map((file, index) => {
              return (
                <Box ref={(el) => (chipRefs.current[index] = el)}>
                  {typeof file === "string" ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={file}
                      icon={<FolderRounded sx={{ fontSize: 16 }} />}
                      onDelete={() => removeTaggedCategory(file)}
                      onMouseDown={(e) => e.preventDefault()}
                    />
                  ) : (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={file.name}
                      icon={
                        <img
                          src={getFileIconImage(file.file_type)}
                          alt="file_icon"
                          width={16}
                          height={16}
                          style={{ borderRadius: "50%" }}
                        />
                      }
                      onDelete={() => removeTaggedFile(file.id)}
                      onMouseDown={(e) => e.preventDefault()}
                    />
                  )}
                </Box>
              );
            })}
          {taggedItems.length > visibleCount && !showAllFiles && (
            <Chip
              size="small"
              variant="outlined"
              label={`+${taggedItems.length - visibleCount} more`}
              onClick={handleShowAllFiles}
              onMouseDown={(e) => e.preventDefault()}
              sx={{ cursor: "pointer" }}
            />
          )}
        </Box>
      )}
      {audioMode ? (
        <Box
          height={66}
          width={"100%"}
          mr={1}
          sx={{
            padding: 0.5,
            paddingBottom: 2,
          }}>
          <Audio
            recordingError={(e) => {
              setAudioMode(false);
              enqueueSnackbar("Permission denied", { variant: "error" });
            }}
            recordingCancelled={(e) => {
              setAudioMode(false);
              enqueueSnackbar("Voice query cancelled", {
                variant: "warning",
              });
            }}
            recordingText={(text) => {
              setAudioMode(false);
              if (text) {
                setQuery(text["text"]);
              } else {
                enqueueSnackbar("Error processing voice query", {
                  variant: "error",
                });
              }
            }}
          />
        </Box>
      ) : (
        <InputBase
          inputRef={queryRef}
          fullWidth
          value={query}
          onChange={handleQueryChange}
          onKeyDown={handleQueryKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={
            isNewSession ? "Search, or ask anything..." : "Ask a follow-up..."
          }
          multiline
          // minRows={isInputFocused ? 3 : 2}
          minRows={2}
          maxRows={4}
          autoFocus
          sx={{
            paddingX: 0.5,
            paddingBottom: 2,
          }}
          slotProps={{
            input: {
              sx: {
                // transition: "height 0.3s ease-in-out",
                "&::placeholder": {
                  color: theme.palette.text.secondary,
                  opacity: 1,
                },
              },
            },
          }}
        />
      )}
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}>
        <Box display={"flex"} alignItems={"center"} gap={1}>
          <FilesFilterDialog
            taggedFiles={taggedFiles}
            addTaggedFiles={addTaggedFiles}
            addTaggedCategories={addTaggedCategories}
            taggedCategories={taggedCategories}
          />
          {/* <Chip
              className={thinkingMode ? classes.activeThinkingChip : null}
              label="Think"
              icon={<PsychologyRounded />}
              onClick={handleThinkingMode}
              onMouseDown={(e) => e.preventDefault()}
            /> */}
        </Box>
        <Box display={"flex"} alignItems={"center"} gap={1}>
          <ThinkingModeChip />
          {query ? (
            <IconButton
              size="small"
              onClick={handleQuerySubmit}
              disabled={!(query && !isStreaming)}
              className={
                query && !isStreaming
                  ? classes.activeSendBtn
                  : classes.disabledSendBtn
              }>
              <SendRounded
                fontSize="small"
                sx={{
                  color:
                    !query || isStreaming
                      ? "inherit"
                      : theme.palette.background.default,
                }}
              />
            </IconButton>
          ) : (
            <IconButton
              onClick={handleClickRecordAudio}
              disabled={audioMode || isStreaming}
              size="small">
              <Tooltip title="Speak your question">
                <KeyboardVoice
                  fontSize="small"
                  sx={{ color: theme.palette.text.primary }}
                />
              </Tooltip>
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default QueryBox;
